import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { imagePresetsApi } from '../api';
import type { PresetCategory, PresetFields } from '../api/imagePresets';

const CATALOG_KEY = ['image-presets'] as const;

export const useImagePresetCatalog = () => useQuery({ queryKey: CATALOG_KEY, queryFn: () => imagePresetsApi.list() });

export const usePresetVersions = (id: string | null) =>
  useQuery({
    queryKey: ['image-presets', 'versions', id],
    queryFn: () => imagePresetsApi.versions(id as string),
    enabled: !!id,
  });

/** 쓰기 하나. 성공하면 목록(버전·used_by 포함)을 다시 읽는다. */
const useCatalogMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY }),
  });
};

export const useCreatePreset = () =>
  useCatalogMutation((a: { fields: Partial<PresetFields>; note?: string }) => imagePresetsApi.create(a.fields, a.note));

export const useUpdatePreset = () =>
  useCatalogMutation((a: { id: string; version: number; changes: Partial<PresetFields>; note?: string }) =>
    imagePresetsApi.update(a.id, a.version, a.changes, a.note),
  );

export const useDeletePreset = () =>
  useCatalogMutation((a: { id: string; version: number }) => imagePresetsApi.remove(a.id, a.version));

export const useReorderPresets = () =>
  useCatalogMutation((a: { category: string; ids: string[] }) => imagePresetsApi.reorder(a.category, a.ids));

export const useRollbackPreset = () =>
  useCatalogMutation((a: { id: string; toVersion: number; version: number }) =>
    imagePresetsApi.rollback(a.id, a.toVersion, a.version),
  );

export const useCreateCategory = () => useCatalogMutation((c: PresetCategory) => imagePresetsApi.createCategory(c));

export const useUpdateCategory = () =>
  useCatalogMutation((a: { id: string; changes: Partial<Omit<PresetCategory, 'id'>> }) =>
    imagePresetsApi.updateCategory(a.id, a.changes),
  );

export const useDeleteCategory = () => useCatalogMutation((id: string) => imagePresetsApi.deleteCategory(id));

export const usePutReference = () =>
  useCatalogMutation((a: { name: string; file: File }) => imagePresetsApi.putReference(a.name, a.file));

export const useDeleteReference = () => useCatalogMutation((name: string) => imagePresetsApi.deleteReference(name));

export const useTrialGeneration = () =>
  useMutation({
    mutationFn: (a: { preset: Partial<PresetFields>; images: File[]; inputs: Record<string, string> }) =>
      imagePresetsApi.trial(a.preset, a.images, a.inputs),
  });

export const useImportPresets = () =>
  useCatalogMutation(
    (a: { bundle: Record<string, unknown>; dryRun: boolean; overwrite?: string[]; overwriteImages?: string[] }) =>
      imagePresetsApi.importBundle(a.bundle, a),
  );

/**
 * 참조 이미지 미리보기 URL. 인증 헤더가 필요해 blob 으로 받아 object URL 을 만든다.
 * sha256 이 바뀌면(교체) 다시 받는다.
 */
export const useReferencePreview = (name: string, sha256: string) => {
  const query = useQuery({
    queryKey: ['image-presets', 'reference', name, sha256],
    queryFn: () => imagePresetsApi.referenceBlob(name),
    staleTime: Infinity,
  });
  const url = useMemo(() => (query.data ? URL.createObjectURL(query.data) : null), [query.data]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return url;
};
